import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import colors from '../../../constants/colors'
import FormLayout from '../FormLayout'
import AddIcon from '@mui/icons-material/Add'
import { useForm, get } from 'react-hook-form'
import TextareaInput from '../../TextareaInput'
import { PhotoCamera } from '@mui/icons-material'
import { FC, Fragment, useCallback, useEffect, useState } from 'react'
import TextInput from '../../TextInput'
import Dropdown from '../../Dropdown'
import selectOptions from '../../../constants/selectOptions'
import { formTypes, IAddPersonForm } from './types'
import constants from './constants'
import { vestResolver } from '@hookform/resolvers/vest'
import ModalTAG from '../../ModalTAG'
import CardTAG from '../../CardTAG'
import { ISubjectDTO, IVictimDTO } from '../../../store/users/types'
import { ChangeEvent } from 'react'
import utils from './utils'
import reporterConstants from '../ReportingPartyForm/constants'
import DateRangePicker from '../../DateRangePicker'

const AddPersonForm: FC<IAddPersonForm> = ({
  updateNumberOfChanges,
  submitFunctions,
  submitFunctionName = '',
  defaultValue,
  personType,
  previewOnly = false,
}) => {
  const [imagePreview, setImagePreview] = useState<string | null>()
  const [openModal, setOpenModal] = useState(false)
  const [customFields, setCustomFields] = useState<string[]>()
  const [changes, setChanges] = useState(0)
  const [originalHireDate, setOriginalHireDate] = useState(null)
  const [terminationDate, setTerminationDate] = useState(null)

  const formMethods = useForm<any>({
    defaultValues: {},
    mode: 'onChange',
    shouldFocusError: false,
  })

  const { register, formState, control, watch, getValues, reset, setValue } = formMethods
  const { dirtyFields } = formState
  const changesNumber = Object.keys(dirtyFields).length

  const submitHandler = () => {
    const { image, ...restOfValues } = getValues()
    if (Object.keys(dirtyFields).length > 0) {
      const newObject = {
        [personType]: { ...restOfValues },
        id: defaultValue?.id,
        custom_fields: Object.keys(customFieldsGetValue()).map(key => {
          return {
            title: key,
            description: customFieldsGetValue()[key],
          }
        }),
        original_hire_date: originalHireDate,
        termination_date: terminationDate,
      }
      if (image instanceof File) {
        return {
          ...newObject,
          [personType]: {
            ...newObject[personType],
            image: image,
          },
        }
      }
      return newObject
    }
  }
  submitFunctions[submitFunctionName].submitFunction = submitHandler

  const customFieldsFormMethods = useForm<any>({
    defaultValues: {},
    mode: 'onChange',
    shouldFocusError: false,
  })

  const {
    register: customFieldsRegister,
    formState: formStateCustomFields,
    reset: resetCustomFields,
    getValues: customFieldsGetValue,
  } = customFieldsFormMethods

  const { dirtyFields: dirtyFieldsCustomFields } = formStateCustomFields
  const changesNumberCustomFields = Object.keys(dirtyFieldsCustomFields).length

  useEffect(() => {
    const defaultValues = utils?.getDefaultValues(personType, defaultValue) ?? {
      person_type: '',
    }
    if (personType === 'victim') {
      const values = defaultValues as IVictimDTO
      const newDefault = {
        ...values?.victim,
      }
      reset(newDefault, {
        keepDirty: true,
        keepDirtyValues: true,
      })
      setValue('image', values?.victim?.image as string)
    }
    if (personType === 'subject') {
      const values = defaultValues as ISubjectDTO
      const newDefault = {
        ...values?.subject,
      }
      reset(newDefault, {
        keepDirty: true,
        keepDirtyValues: true,
      })
      setValue('image', values?.subject?.image as string)
    }

    const fields = defaultValue?.custom_fields?.map((field: { title: any }) => field?.title)
    const transformedObject = defaultValue?.custom_fields?.reduce(
      (result: { [x: string]: any }, item: { title: any; description: any }) => {
        const key = item?.title
        result[key] = item?.description
        return result
      },
      {},
    )
    resetCustomFields(transformedObject, {
      keepDirty: true,
      keepDirtyValues: true,
    })
    setCustomFields(fields)
  }, [])

  useEffect(() => {
    const counter = changesNumber + changesNumberCustomFields
    if (counter == changes) return
    if (counter !== changes) {
      updateNumberOfChanges(counter - changes, submitFunctionName)
      setChanges(counter)
    }
  }, [formState, formStateCustomFields])

  const imageWatchValue = watch('image')
  const subjectTypeWatchValue = watch('person_type')

  const addNewTagFormMethods = useForm<any>({
    defaultValues: { newTag: '' },
    mode: 'onChange',
    shouldFocusError: false,
    resolver: vestResolver(utils.newTagFormValidation),
  })
  const {
    register: newTagRegister,
    handleSubmit: newTagHandleSubmit,
    formState: formStateNewTag,
    reset: resetNewTag,
  } = addNewTagFormMethods

  useEffect(() => {
    if (typeof imageWatchValue === 'string') {
      setImagePreview(imageWatchValue === '' ? '' : imageWatchValue)
      return
    }
    if (imageWatchValue instanceof File) {
      setImagePreview(URL.createObjectURL(imageWatchValue))
    }
  }, [imageWatchValue])

  const getMessage = useCallback(
    (field: formTypes) => {
      const error = get(formState.errors, field)

      if (!error) {
        return ''
      }

      const { message, ...nestedError } = error
      const errorType = typeof error

      if (errorType === 'string') {
        return error
      }

      if (errorType === 'object' && message) {
        return message
      }

      const nestedField = Object.keys(nestedError)[0]
      const nestedErrorMessage = nestedError[nestedField]?.message || ''
      return nestedErrorMessage
    },
    [formState],
  )

  const getMessageNewTag = useCallback(
    (field: 'newTag') => {
      if (!formStateNewTag.errors[field]) return ''

      return formStateNewTag.errors[field]!.message
    },
    [formState],
  )

  const addNewTagSubmitHandler = (values: { newTag: string }) => {
    const oldTags = customFieldsGetValue()
    const { newTag } = values
    const propertyName = utils.camalize(newTag)
    const newState = {
      ...oldTags,
      [propertyName]: '',
    }
    setOpenModal(false)
    const updatedCustomFields = customFields ? [...customFields, newTag] : [newTag]
    setCustomFields(updatedCustomFields)
    resetCustomFields(newState, {
      keepDirty: true,
      keepDirtyValues: true,
    })
    resetNewTag({ newTag: '' })
  }

  const openModalHandler = () => {
    setOpenModal(true)
  }

  // const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
  //   if (e?.target?.files) {
  //     const file = e?.target?.files[0]
  //     setValue('image', file) // Set the value of the "file" field to the selected File object
  //   }
  // }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      const file = e.target.files[0];
      if (file) {
        setValue('image', file, { shouldDirty: true });

        // 🚀 Mark as a change so Save button activates
        updateNumberOfChanges(1, personType === 'victim' ? 'victims:' : 'subject:');
      }
    }
  };

  const renderFields = () => {
    return reporterConstants.textFieldBasedOnSubjectType[subjectTypeWatchValue].map(field => {
      if (!['original_hire_date', 'termination_date'].includes(field.fieldName)) {
        return (
          <TextInput
            register={register}
            {...field}
            error={getMessage(field.fieldName) as string}
            key={field.fieldName}
            disabled={previewOnly}
          />
        )
      }

      if (field.fieldName === 'original_hire_date') {
        return (
          <Fragment key={field.fieldName}>
            <DateRangePicker
              label={field.label}
              disablePresets={true}
              setDate={setOriginalHireDate}
              date={originalHireDate}
              name={field.fieldName}
            />
          </Fragment>
        )
      }

      if (field.fieldName === 'termination_date') {
        return (
          <Fragment key={field.fieldName}>
            <DateRangePicker
              label={field.label}
              disablePresets={true}
              setDate={setTerminationDate}
              date={terminationDate}
              name={field.fieldName}
            />
          </Fragment>
        )
      }
    })
  }

  const renderCustomFields = () => {
    const customFiledsValues = customFieldsGetValue()
    return (
      customFields &&
      Object.keys(customFiledsValues).map(key => {
        return (
          <TextareaInput
            label={key}
            key={key}
            register={customFieldsRegister}
            fieldName={key}
            minRows={5}
            placeholder={'Write a brief message'}
            previewOnly={previewOnly}
          />
        )
      })
    )
  }

  const resolveForm = () => {
    if (!subjectTypeWatchValue) {
      return (
        <Dropdown
          control={control}
          label='Subject Type'
          fieldName={'person_type'}
          options={selectOptions.subjectTypes}
          selectedValue={subjectTypeWatchValue}
          placeholder='Select a Option'
        />
      )
    }
    return (
      <Box>
        <Box className='t-hbox t-gap-18'>
          <Grid item xs={12} sm={4}>
            <Stack sx={constants.imageUploadRowStyle}>
              {!imagePreview && (
                <Box sx={constants.imageMessageStyle}>
                  <Typography sx={{ textAlign: 'center' }}>Upload Profile Image</Typography>
                </Box>
              )}
              {imagePreview && (
                <Box sx={{ width: { xs: '100%' }, height: { xs: '100%' } }}>
                  <img
                    alt={'vicitim / subject image'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '5px',
                    }}
                    src={imagePreview}
                  />
                </Box>
              )}
              <Button
                variant='submitFormButton'
                component='label'
                sx={{ mt: '18px' }}
                endIcon={<PhotoCamera />}
                disabled={previewOnly}
              >
                <Typography variant='bodyBold'>
                  {imagePreview ? 'Change Image' : 'Upload Image'}
                </Typography>
                <input hidden accept='image/*' type='file' onChange={handleFileChange} />
              </Button>
            </Stack>
          </Grid>
          <Box className={'t-vbox t-gap-18 t-flex-1'}>
            <FormLayout>
              <Dropdown
                control={control}
                label='Subject Type'
                fieldName={'person_type'}
                options={selectOptions.subjectTypes}
                selectedValue={subjectTypeWatchValue}
                placeholder='Select a Option'
                previewOnly={previewOnly}
              />
              {renderFields()}
              <TextareaInput
                label={'Description'}
                register={register}
                fieldName={'description'}
                minRows={5}
                placeholder={'Write a brief message'}
                previewOnly={previewOnly}
              />
              {renderCustomFields()}
              <Button
                variant='outlined'
                startIcon={<AddIcon />}
                sx={{ color: colors.BLACK }}
                onClick={openModalHandler}
                disabled={previewOnly}
              >
                <Typography variant='bodyRegular' color='inherit'>
                  Add Custom Text Field
                </Typography>
              </Button>
            </FormLayout>
          </Box>
        </Box>
        <ModalTAG open={openModal}>
          <form onSubmit={newTagHandleSubmit(addNewTagSubmitHandler)}>
            <CardTAG
              title={'Add Custom Field Title'}
              closable={true}
              closeCard={() => {
                setOpenModal(false)
              }}
            >
              <FormLayout>
                <TextInput
                  register={newTagRegister}
                  fieldName={'newTag'}
                  label={'Title'}
                  error={getMessageNewTag('newTag') as string}
                />
                <Button variant='submitFormButton' type='submit'>
                  <Typography variant='bodyBold'>Add</Typography>
                </Button>
              </FormLayout>
            </CardTAG>
          </form>
        </ModalTAG>
      </Box>
    )
  }

  return <>{resolveForm()}</>
}

export default AddPersonForm
